import { PageContext } from '@contexts/page'
import { SubscriptionFormVisibleContext } from '@contexts/subscription-form-visible'
import { ContentType } from '@entries/content-types'
import ContentfulCtaNavbar from '@entries/contentful-cta-navbar'
import { useInternalLinkProps } from '@hooks/links/use-internal-link-props'
import { useMatchMedia } from '@hooks/use-match-media'
import { useTrackExternalLink } from '@hooks/use-track-external-link'
import { Entry } from '@resolvers/entry'
import Button from '@ui/atoms/button'
import { EnhancedButton } from '@ui/enhanced-components/enhanced-components'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import { device } from '@utils/breakpoints'
import clsx from 'clsx/lite'
import React, {
  forwardRef,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { animateScroll as scroll } from 'react-scroll'

type NavbarCtaProps = {
  isLandingPage?: boolean
  cta?: Queries.EntryFragment
}

export const NavbarCta = forwardRef<HTMLDivElement, NavbarCtaProps>(
  ({ cta, isLandingPage = false }, ref) => {
    const { open } = useContext(SubscriptionFormVisibleContext)
    const trackExternalHyperlink = useTrackExternalLink()
    const isNotMobile = useMatchMedia(device.tablet)
    const [isScrolled, setIsScrolled] = useState(false)

    const { form } = useContext(PageContext) || {}
    const openDialogForm = useCallback(
      (event: SyntheticEvent) => {
        open(true)
        trackExternalHyperlink(event)
      },
      [trackExternalHyperlink, open]
    )

    const scrollToTop = useCallback(
      (event: SyntheticEvent) => {
        scroll.scrollToTop()
        trackExternalHyperlink(event)
      },
      [trackExternalHyperlink]
    )

    useEffect(() => {
      const isAlreadyTopScroll = () => {
        if (window?.scrollY > 0) {
          setIsScrolled(true)
        } else {
          setIsScrolled(false)
        }
      }

      window.addEventListener('scroll', isAlreadyTopScroll)
      return () => {
        window.removeEventListener('scroll', isAlreadyTopScroll)
      }
    }, [])

    const ctaRenderer = () => {
      if (isLandingPage && form) {
        return (
          <EnhancedButton
            data-role={ENHANCED_COMPONENT_ROLE.STICKY_FORM}
            data-type={ENHANCED_COMPONENT_TYPE.CTA}
            id="cta_sticky_form"
            size="small"
            className={clsx(
              'text-075 tablet:opacity-0 w-fit! rounded-full bg-[#8FD448]! px-[20px] py-[10px] text-center font-medium text-[#003500]! opacity-100 transition-opacity! duration-700',
              isScrolled && 'tablet:opacity-100'
            )}
            onClick={isNotMobile ? scrollToTop : openDialogForm}
          >
            Ouvrir un compte
          </EnhancedButton>
        )
      }

      if (!isLandingPage && !cta) {
        return (
          <Button
            size="small"
            {...useInternalLinkProps('/ouvrir')}
            className="text-075 w-fit! rounded-full bg-[#8FD448]! px-[20px] py-[10px] text-center font-medium text-[#003500]!"
          >
            Souscrire
          </Button>
        )
      }

      return (
        <Entry
          content={cta}
          mapping={{
            [ContentType.ContentfulCta]: ContentfulCtaNavbar,
          }}
        />
      )
    }

    return (
      <div
        ref={ref}
        className={clsx(
          'laptop:static laptop:translate-x-0 absolute left-1/2 -translate-x-1/2 self-center',
          isLandingPage && 'right-4 left-auto! translate-x-0!'
        )}
      >
        {ctaRenderer()}
      </div>
    )
  }
)
